import React, { lazy, Suspense } from 'react';
import { Spin } from 'antd';
import { Switch, Route, Redirect } from 'react-router-dom';
import withAdminLayout from '../layout/withAdminLayout';

const SignUp        = lazy(() => import('../container/profile/authentication/overview/Signup'));
const FbLogin       = lazy(() => import('../container/profile/authentication/overview/FbSignIn'));
const FbSignUp      = lazy(() => import('../container/profile/authentication/overview/FbSignup'));
const ForgotPass    = lazy(() => import('../container/profile/authentication/overview/ForgotPassword'));
const VerifyUser    = lazy(() => import('../container/profile/authentication/overview/VerifyUser'));
const LadingPage    = lazy(() => import('../container/home/LadingPage'));
const Calendar      = lazy(() => import('../container/calendar/Calendar'));
const JoinPublicEvent = lazy(() => import('../container/calendar/overview/JoinPublicEvent'));

/* ── Static content pages ─────────────────────────────────────────
 * Plain single-segment routes — no ordering risk relative to the
 * /:id/:title catch-all below (that wildcard only ever matches
 * exactly two segments), but grouped here for visibility since
 * they're new. */
const AboutUs            = lazy(() => import('../container/pages/AboutUs'));
const Guide              = lazy(() => import('../container/pages/Guide'));
const ContactUs          = lazy(() => import('../container/pages/ContactUs'));
const PrivacyPolicy      = lazy(() => import('../container/pages/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('../container/pages/TermsAndConditions'));

/* ── Tennis pages ─────────────────────────────────────────────────
 * Registered here, alongside the event module's own routes, instead
 * of as a separate standalone <Router> in App.js. That gets them:
 *   (a) the same withAdminLayout chrome every other page here uses
 *       (no bespoke tennis-only header), and
 *   (b) the same Router/history as CreateMatchModal.jsx, so
 *       history.push('/match-details') actually navigates instead of
 *       pushing a URL a sibling, untethered Router never sees.
 *
 * /tennis-admin used to be its own OTP-gated "My Matches" list, with
 * its own OTP step separate from events' /view-events. That's gone —
 * /view-events (VerifyUser.jsx) now has an Events/Matches/Meetings tab
 * switcher and covers all three from the ONE OTP flow (MyEvents.jsx's
 * modal), since the same view_token already works against
 * /get-all-events, /tennis/my-matches, AND /onetoone/my-meetings.
 * /tennis-admin just redirects there now, so any old links/bookmarks
 * still land somewhere useful.
 *
 * /match-details   — the match "details page", the tennis equivalent
 *                    of /:id/:title → Calendar.js.
 * /join-block/:token — a public block's share link (set via the
 *                    Public/Private toggle when creating/editing a
 *                    block). Anyone with the link can verify their
 *                    email with an OTP and join — see
 *                    JoinPublicBlock.jsx.
 * /join-event/:token — the event equivalent, for an EVENT's own
 *                    Public/Private toggle (AddNewEvent.js /
 *                    UpdateEvent.js). Public: anyone can verify and
 *                    join. Private: same link, but only emails
 *                    already on the invite list get through — see
 *                    JoinPublicEvent.jsx.
 *
 * CRITICAL: all of these must precede /:id/:title below — that
 * wildcard matches ANY two-segment path, so without this ordering
 * /join-block/39 gets swallowed by it and treated as an event link
 * (this was the "jwt malformed" bug — /join-block/39 fell through to
 * Calendar.js, which stored the literal string "join-block" as an
 * auth token and tried to use it). */
const MatchDetails    = lazy(() => import('../container/tennis/MatchDetails'));
const JoinPublicBlock = lazy(() => import('../container/tennis/JoinPublicBlock'));

/* ── One-to-one booking pages ─────────────────────────────────────
 * The creation flow (CreateOneToOneModal.jsx, VerifyUser.jsx's toolbar
 * button / Meetings tab) mints and emails both links below.
 *
 * /book/:token           — the PUBLIC booking page (share_token). No
 *                          OTP gate, matches Calendly's own public
 *                          booking page — see the long comment above
 *                          the ONE-TO-ONE MEETING MODULE section in
 *                          index.js for why that's intentional.
 * /onetoone-admin/:token — the host's management page (admin_token):
 *                          list of bookings, confirm/decline pending
 *                          ones, and the "Block Time Off" panel.
 *
 * Both now ALSO exist in a second, 3-segment form with a readable
 * title slug ahead of the token —
 *   /book/:slug/:token
 *   /onetoone-admin/:slug/:token
 * — so a shared link reads as e.g. /book/free-art-consultation/eyJhbGc…
 * instead of just the bare JWT. slug is PURELY decorative: every route
 * below resolves entirely from :token (BookingPage.jsx / OnetooneAdminPage.jsx
 * both just do useParams().token — the slug segment, if present, is
 * simply never read), same as the existing /:id/:title convention
 * ignores :title. See slugifyTitle in index.js for how the slug itself
 * is generated.
 *
 * The plain 2-segment /book/:token and /onetoone-admin/:token routes
 * are kept side by side with the new 3-segment ones — NOT replaced —
 * because links already emailed out before this change only have the
 * bare-token form. Removing them would break every link sent before
 * today.
 *
 * CRITICAL: /book/:token and /onetoone-admin/:token are 2-segment
 * paths, so they MUST precede /:id/:title, same reasoning as
 * /join-block/:token and /match-details above — the wildcard would
 * otherwise silently swallow them. The 3-segment /:slug/:token forms
 * are UNAFFECTED by that wildcard (it only ever matches exactly two
 * segments), so their position relative to /:id/:title doesn't matter,
 * but they're kept right next to their 2-segment counterparts here for
 * readability. */
const BookingPage       = lazy(() => import('../container/onetoone/BookingPage'));
const OnetooneAdminPage = lazy(() => import('../container/onetoone/OnetooneAdminPage'));

/* NOTE: super-admin routes (/superadmin/*) are intentionally NOT here.
 * They're handled by SuperAdminApp in App.js, which renders them as
 * fully standalone pages with no layout wrapper and no Redux/Firebase
 * dependency. Any path starting with /superadmin is intercepted
 * before this router runs. */

function FrontendRoutes() {
  return (
    <Suspense
      fallback={
        <div className="spin">
          <Spin />
        </div>
      }
    >
      <Switch>
        {/* Static named routes — all must come before /:id/:title wildcard */}
        <Route exact path="/"               component={LadingPage} />
        <Route exact path="/forgotPassword" component={ForgotPass} />
        <Route exact path="/register"       component={SignUp} />
        <Route exact path="/fbRegister"     component={FbSignUp} />
        <Route exact path="/fbSignIn"       component={FbLogin} />
        <Route exact path="/view-events"    component={VerifyUser} />
        <Route path="/calendar"             component={Calendar} />
        <Route exact path="/join-event/:token" component={JoinPublicEvent} />

        {/* Static content pages */}
        <Route exact path="/about"                 component={AboutUs} />
        <Route exact path="/guide"                 component={Guide} />
        <Route exact path="/contact-us"             component={ContactUs} />
        <Route exact path="/privacy-policy"         component={PrivacyPolicy} />
        <Route exact path="/terms-and-conditions"   component={TermsAndConditions} />

        {/* Tennis — must also precede /:id/:title (both are 1–2
         * segment paths that would otherwise be swallowed by it) */}
        <Route exact path="/tennis-admin">
          <Redirect to="/view-events" />
        </Route>
        <Route exact path="/match-details/:token" component={MatchDetails} />
        <Route exact path="/match-details"        component={MatchDetails} />
        <Route exact path="/join-block/:token"    component={JoinPublicBlock} />

        {/* One-to-one booking — the 2-segment forms must also precede
         * /:id/:title, same reasoning as tennis above. The 3-segment
         * /:slug/:token forms are immune to that wildcard but are kept
         * here alongside their 2-segment counterparts for readability. */}
        <Route exact path="/book/:token"                  component={BookingPage} />
        <Route exact path="/book/:slug/:token"             component={BookingPage} />
        <Route exact path="/onetoone-admin/:token"         component={OnetooneAdminPage} />
        <Route exact path="/onetoone-admin/:slug/:token"   component={OnetooneAdminPage} />

        {/* /:id/:title LAST — catches the public calendar share links.
         * Every multi-segment named route above must precede this. */}
        <Route exact path="/:id/:title"     component={Calendar} />
      </Switch>
    </Suspense>
  );
}

export default withAdminLayout(FrontendRoutes);